import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchOnboardingStatus, peekOnboardingStatus, type OnboardingStatus } from '../lib/onboardingStatus';

interface RequireOnboardingProps {
  children: React.ReactNode;
  /** Test/dev escape hatch; mirrors VITE_BYPASS_AUTH in App.tsx. */
  bypass?: boolean;
}

type GateState = OnboardingStatus | 'checking' | 'unavailable';

/**
 * First-login gate for the signed-in app shell. A user with no onboarding row
 * is sent to /onboarding (where Skip is always one tap away); completed and
 * skipped users pass straight through. A failed status check lets the user in
 * rather than locking them out of the whole app: the gate is a nudge, and
 * OnboardingPage itself is the surface that must never overwrite saved answers.
 *
 * Supabase hands AuthContext a new `user` object on every auth event (token
 * refresh, tab refocus). The gate keys on the user id and seeds from the cache,
 * so those events never swap the mounted shell for a spinner mid-practice.
 */
export default function RequireOnboarding({ children, bypass = false }: RequireOnboardingProps) {
  const { user } = useAuth();
  const userId = user?.id;
  const location = useLocation();
  const [state, setState] = useState<{ userId?: string; status: GateState }>(() => ({
    userId,
    status: userId ? peekOnboardingStatus(userId) ?? 'checking' : 'checking',
  }));

  useEffect(() => {
    if (bypass || !userId) return;
    let cancelled = false;
    const known = peekOnboardingStatus(userId);
    if (known) {
      setState({ userId, status: known });
      return;
    }
    setState({ userId, status: 'checking' });
    fetchOnboardingStatus(userId)
      .then((next) => {
        if (!cancelled) setState({ userId, status: next });
      })
      .catch((error: unknown) => {
        console.error('Unable to check Howie onboarding status:', error);
        if (!cancelled) setState({ userId, status: 'unavailable' });
      });
    return () => {
      cancelled = true;
    };
  }, [bypass, userId]);

  if (bypass || !userId) return <>{children}</>;

  // A state from a previous user id is stale; treat it as checking.
  const status = state.userId === userId ? state.status : 'checking';

  if (status === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-how-paper-soft">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  if (status === 'needed') {
    return <Navigate to="/onboarding" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
